/**
 * Billing Page Component
 *
 * Balance-based subscription management with top-ups, transactions,
 * usage statistics, and invoice history
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  getBillingPlans,
  getSubscription,
  getUsage,
  getInvoices,
  subscribeToPlan,
  confirmPayment,
  downloadInvoicePdf,
  getBalance,
  topUpBalance,
  getTransactions,
  getOveragePreview,
  getPaymentMethods,
  addPaymentMethod,
  setDefaultPaymentMethod,
  deletePaymentMethod,
  type BillingPlan,
  type Subscription,
  type UsageStats,
  type Invoice,
  type Balance,
  type BalanceTransaction,
  type OverageInfo,
  type PaymentMethod,
} from '../services/api';

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('billing');
  const { t: tc } = useTranslation('common');
  const { user, logout } = useAuth();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // UI State
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan | null>(null);
  const [paymentMessage, setPaymentMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState<string | null>(null);
  const [topUpAmount, setTopUpAmount] = useState<number>(50);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [isTopingUp, setIsTopingUp] = useState(false);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'transactions' | 'invoices'>('transactions');

  // Card form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpMonth, setCardExpMonth] = useState('');
  const [cardExpYear, setCardExpYear] = useState('');
  const [cardholderName, setCardholderName] = useState('');

  // Data State
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<BillingPlan | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
  const [overage, setOverage] = useState<OverageInfo | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  // Handle invoice PDF download
  const handleDownloadInvoice = async (invoiceId: string) => {
    setDownloadingInvoice(invoiceId);
    try {
      await downloadInvoicePdf(invoiceId);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      setPaymentMessage({ type: 'error', text: t('documents.downloadPdf') });
    } finally {
      setDownloadingInvoice(null);
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Load billing data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [plansData, subData, usageData, invoicesData, balanceData, txData, overageData, pmData] = await Promise.all([
          getBillingPlans(),
          getSubscription(),
          getUsage(),
          getInvoices(),
          getBalance(),
          getTransactions(20, 0),
          getOveragePreview(),
          getPaymentMethods()
        ]);
        setPlans(plansData.plans);
        setSubscription(subData.subscription);
        setCurrentPlan(subData.plan);
        setUsage(usageData.usage);
        setInvoices(invoicesData.invoices);
        setBalance(balanceData.balance);
        setTransactions(txData.transactions);
        setOverage(overageData.overage);
        setPaymentMethods(pmData.payment_methods);
        // Set default payment method as selected
        const defaultPm = pmData.payment_methods.find(pm => pm.is_default);
        if (defaultPm) {
          setSelectedPaymentMethodId(defaultPm.id);
        }
      } catch (error) {
        console.error('Failed to load billing data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Handle payment callback from URL
  useEffect(() => {
    const payment = searchParams.get('payment');
    const planId = searchParams.get('plan');
    const paymentIntent = searchParams.get('payment_intent');

    if (payment === 'success' && planId && paymentIntent) {
      setIsProcessingPayment(true);
      setPaymentMessage({ type: 'info', text: t('messages.confirmingPayment') });

      confirmPayment(planId, paymentIntent)
        .then(async (result) => {
          setPaymentMessage({ type: 'success', text: result.message || t('messages.paymentSuccessful') });
          // Reload data
          const [subData, invoicesData, balanceData, txData] = await Promise.all([
            getSubscription(),
            getInvoices(),
            getBalance(),
            getTransactions(20, 0)
          ]);
          setSubscription(subData.subscription);
          setCurrentPlan(subData.plan);
          setInvoices(invoicesData.invoices);
          setBalance(balanceData.balance);
          setTransactions(txData.transactions);
        })
        .catch((error) => {
          console.error('Payment confirmation failed:', error);
          setPaymentMessage({ type: 'error', text: t('messages.paymentFailed') });
        })
        .finally(() => {
          setIsProcessingPayment(false);
          setSearchParams({});
        });
    } else if (payment === 'cancelled') {
      setPaymentMessage({ type: 'error', text: t('messages.paymentCancelled') });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleTopUp = async () => {
    if (topUpAmount < 5 || topUpAmount > 10000) {
      setPaymentMessage({ type: 'error', text: t('messages.amountError') });
      return;
    }

    setIsTopingUp(true);
    try {
      const result = await topUpBalance(topUpAmount, 'card', selectedPaymentMethodId || undefined);
      setPaymentMessage({ type: 'success', text: result.message });
      setShowTopUpModal(false);

      // Reload balance and transactions
      const [balanceData, txData, invoicesData] = await Promise.all([
        getBalance(),
        getTransactions(20, 0),
        getInvoices()
      ]);
      setBalance(balanceData.balance);
      setTransactions(txData.transactions);
      setInvoices(invoicesData.invoices);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to process top-up';
      setPaymentMessage({ type: 'error', text: message });
    } finally {
      setIsTopingUp(false);
    }
  };

  const handleAddCard = async () => {
    // Validate card details
    const cleanNumber = cardNumber.replace(/\s/g, '');
    if (cleanNumber.length < 13 || cleanNumber.length > 19) {
      setPaymentMessage({ type: 'error', text: t('messages.invalidCard') });
      return;
    }
    const expMonth = parseInt(cardExpMonth, 10);
    const expYear = parseInt(cardExpYear, 10);
    if (expMonth < 1 || expMonth > 12) {
      setPaymentMessage({ type: 'error', text: t('messages.invalidMonth') });
      return;
    }
    if (expYear < 2024 || expYear > 2050) {
      setPaymentMessage({ type: 'error', text: t('messages.invalidYear') });
      return;
    }

    setIsAddingCard(true);
    try {
      const result = await addPaymentMethod({
        card_number: cleanNumber,
        exp_month: expMonth,
        exp_year: expYear,
        cardholder_name: cardholderName || undefined,
        set_as_default: paymentMethods.length === 0 // Set as default if first card
      });

      setPaymentMethods(prev => [result.payment_method, ...prev]);
      setPaymentMessage({ type: 'success', text: t('messages.cardAdded') });
      setShowAddCardModal(false);
      // Clear form
      setCardNumber('');
      setCardExpMonth('');
      setCardExpYear('');
      setCardholderName('');
      // Select the new card
      setSelectedPaymentMethodId(result.payment_method.id);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to add card';
      setPaymentMessage({ type: 'error', text: message });
    } finally {
      setIsAddingCard(false);
    }
  };

  const handleSetDefaultCard = async (paymentMethodId: string) => {
    try {
      const result = await setDefaultPaymentMethod(paymentMethodId);
      // Update payment methods list
      setPaymentMethods(prev => prev.map(pm => ({
        ...pm,
        is_default: pm.id === paymentMethodId
      })));
      setSelectedPaymentMethodId(result.payment_method.id);
      setPaymentMessage({ type: 'success', text: t('messages.defaultUpdated') });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to set default';
      setPaymentMessage({ type: 'error', text: message });
    }
  };

  const handleDeleteCard = async (paymentMethodId: string) => {
    setDeletingPaymentMethod(paymentMethodId);
    try {
      await deletePaymentMethod(paymentMethodId);
      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
      // If deleted the selected one, select the new default
      if (selectedPaymentMethodId === paymentMethodId) {
        const remaining = paymentMethods.filter(pm => pm.id !== paymentMethodId);
        const newDefault = remaining.find(pm => pm.is_default) || remaining[0];
        setSelectedPaymentMethodId(newDefault?.id || null);
      }
      setPaymentMessage({ type: 'success', text: t('messages.cardRemoved') });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to remove card';
      setPaymentMessage({ type: 'error', text: message });
    } finally {
      setDeletingPaymentMethod(null);
    }
  };

  const getCardIcon = (brand: string) => {
    switch (brand) {
      case 'Visa':
        return <span className="text-xs font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">VISA</span>;
      case 'Mastercard':
        return <span className="text-xs font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">MC</span>;
      case 'Amex':
        return <span className="text-xs font-bold text-blue-800 bg-blue-100 px-1.5 py-0.5 rounded">AMEX</span>;
      default:
        return <span className="text-xs font-bold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">CARD</span>;
    }
  };

  const handleUpgrade = (plan: BillingPlan) => {
    setSelectedPlan(plan);
    setShowUpgradeModal(true);
  };

  const confirmUpgrade = async () => {
    if (!selectedPlan) return;
    setIsSubscribing(selectedPlan.id);
    try {
      const result = await subscribeToPlan(selectedPlan.id, true);
      setSubscription(result.subscription);
      setCurrentPlan(result.plan);
      setShowUpgradeModal(false);

      if (result.charged) {
        setPaymentMessage({ type: 'success', text: t('messages.subscribedSuccess', { plan: selectedPlan.name, amount: result.charged.amount_formatted }) });
      } else {
        setPaymentMessage({ type: 'success', text: t('messages.switchedSuccess', { plan: selectedPlan.name }) });
      }

      // Reload data
      const [usageData, invoicesData, balanceData, txData] = await Promise.all([
        getUsage(),
        getInvoices(),
        getBalance(),
        getTransactions(20, 0)
      ]);
      setUsage(usageData.usage);
      setInvoices(invoicesData.invoices);
      setBalance(balanceData.balance);
      setTransactions(txData.transactions);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to process subscription';
      // Check if it's insufficient balance
      if (message.includes('Insufficient balance')) {
        setPaymentMessage({ type: 'error', text: t('messages.insufficientBalance') });
      } else {
        setPaymentMessage({ type: 'error', text: message });
      }
    } finally {
      setIsSubscribing(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatUsagePercent = (used: number, limit: number): number => {
    if (limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const formatLimit = (value: number): string => {
    if (value === -1) return t('usage.unlimited');
    return value.toLocaleString();
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'TOP_UP':
        return <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
      case 'SUBSCRIPTION':
        return <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>;
      case 'OVERAGE':
        return <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>;
      case 'REFUND':
        return <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>;
      default:
        return <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/voice')}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="hidden sm:inline text-sm font-medium">{tc('buttons.back')}</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t('title')}</h1>
                <p className="text-xs text-gray-500">{t('subtitle')}</p>
              </div>
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                  <button onClick={() => { setUserMenuOpen(false); navigate('/settings'); }} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">{tc('navigation.settings')}</button>
                  <button onClick={() => { setUserMenuOpen(false); navigate('/support'); }} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">{tc('navigation.support')}</button>
                  {user?.role === 'admin' && <button onClick={() => { setUserMenuOpen(false); navigate('/admin'); }} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50">{tc('navigation.adminPanel')}</button>}
                  <div className="border-t border-gray-100 my-1" />
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50">{tc('navigation.logout')}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Payment Message Banner */}
        {(paymentMessage || isProcessingPayment) && (
          <div
            className={`rounded-xl p-4 flex items-center gap-3 ${
              isProcessingPayment ? 'bg-blue-50 border border-blue-200' :
              paymentMessage?.type === 'success' ? 'bg-green-50 border border-green-200' :
              paymentMessage?.type === 'error' ? 'bg-red-50 border border-red-200' :
              'bg-blue-50 border border-blue-200'
            }`}
          >
            {isProcessingPayment ? (
              <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
            ) : paymentMessage?.type === 'success' ? (
              <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <p className={`text-sm font-medium ${
              isProcessingPayment ? 'text-blue-800' :
              paymentMessage?.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {isProcessingPayment ? t('messages.confirmingPayment') : paymentMessage?.text}
            </p>
            {paymentMessage && !isProcessingPayment && (
              <button onClick={() => setPaymentMessage(null)} className="ml-auto text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Balance Card */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-emerald-100 text-sm mb-1">{t('balance.yourBalance')}</p>
              <h2 className="text-4xl font-bold">{balance?.amount_formatted || '€0.00'}</h2>
              <p className="text-emerald-100 mt-2 text-sm">
                {t('balance.availableFor')}
              </p>
            </div>
            <button
              onClick={() => setShowTopUpModal(true)}
              className="px-6 py-3 bg-white text-emerald-700 font-semibold rounded-xl hover:bg-emerald-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('balance.topUpBalance')}
            </button>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('paymentMethods.title')}</h3>
            <button
              onClick={() => setShowAddCardModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('paymentMethods.addCard')}
            </button>
          </div>

          {paymentMethods.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <p className="text-gray-500 mb-3">{t('paymentMethods.noMethods')}</p>
              <button
                onClick={() => setShowAddCardModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('paymentMethods.addFirst')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                    pm.is_default ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {getCardIcon(pm.brand)}
                    <div>
                      <p className="font-medium text-gray-900">
                        •••• •••• •••• {pm.last4}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t('paymentMethods.expires')} {pm.exp_month.toString().padStart(2, '0')}/{pm.exp_year}
                        {pm.cardholder_name && ` • ${pm.cardholder_name}`}
                      </p>
                    </div>
                    {pm.is_default && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        {t('paymentMethods.default')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!pm.is_default && (
                      <button
                        onClick={() => handleSetDefaultCard(pm.id)}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {t('paymentMethods.setDefault')}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteCard(pm.id)}
                      disabled={deletingPaymentMethod === pm.id}
                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title={t('paymentMethods.removeCard')}
                    >
                      {deletingPaymentMethod === pm.id ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current Plan */}
        <div className="hidden bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-blue-100 text-sm mb-1">{t('plan.currentPlan')}</p>
              <h2 className="text-2xl font-bold">{currentPlan?.name || 'Free'}</h2>
              <p className="text-blue-100 mt-1">{currentPlan?.description}</p>
            </div>
            <div className="text-right" style={{ display: 'none' }}>
              <p className="text-3xl font-bold">€{currentPlan?.price_monthly || 0}</p>
              <p className="text-blue-100">/month</p>
              {subscription?.status === 'active' && (
                <span className="inline-block mt-2 px-3 py-1 bg-green-500/20 text-green-100 rounded-full text-sm">
                  {t('plan.active')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Usage Stats */}
        {usage && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('usage.title')}</h3>
              {overage && overage.total_cents > 0 && (
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                  {t('usage.overage')}: {overage.total_formatted}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Voice Minutes */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">{t('usage.voiceMinutes')}</span>
                  <span className="font-medium">{usage.voice_minutes_used} / {formatLimit(usage.voice_minutes_limit)}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      formatUsagePercent(usage.voice_minutes_used, usage.voice_minutes_limit) > 80 ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${formatUsagePercent(usage.voice_minutes_used, usage.voice_minutes_limit)}%` }}
                  />
                </div>
              </div>

              {/* API Calls */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">{t('usage.apiCalls')}</span>
                  <span className="font-medium">{usage.api_calls_used.toLocaleString()} / {formatLimit(usage.api_calls_limit)}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      formatUsagePercent(usage.api_calls_used, usage.api_calls_limit) > 80 ? 'bg-red-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${formatUsagePercent(usage.api_calls_used, usage.api_calls_limit)}%` }}
                  />
                </div>
              </div>

              {/* Storage */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">{t('usage.storage')}</span>
                  <span className="font-medium">{usage.storage_used_mb.toFixed(1)} MB / {formatLimit(usage.storage_limit_mb)} MB</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      formatUsagePercent(usage.storage_used_mb, usage.storage_limit_mb) > 80 ? 'bg-red-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${formatUsagePercent(usage.storage_used_mb, usage.storage_limit_mb)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Available Plans */}
        <div className="hidden">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('plan.availablePlans')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-xl shadow-sm border-2 p-6 transition-all ${
                  plan.id === subscription?.plan_id
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <h4 className="text-xl font-bold text-gray-900">{plan.name}</h4>
                <p className="text-gray-500 text-sm mt-1">{plan.description}</p>
                <div className="mt-4" style={{ display: 'none' }}>
                  <span className="text-3xl font-bold text-gray-900">€{plan.price_monthly}</span>
                  <span className="text-gray-500">/mo</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleUpgrade(plan)}
                  disabled={plan.id === subscription?.plan_id || isSubscribing === plan.id}
                  className={`w-full mt-6 py-2 px-4 rounded-lg font-medium transition-colors ${
                    plan.id === subscription?.plan_id
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {plan.id === subscription?.plan_id
                    ? t('plan.currentPlan')
                    : isSubscribing === plan.id
                      ? t('plan.subscribing')
                      : plan.price_monthly > (currentPlan?.price_monthly || 0)
                        ? t('plan.upgrade')
                        : t('plan.downgrade')}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Transaction & Invoice History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActiveTab('transactions')}
                className={`text-sm font-medium pb-1 border-b-2 transition-colors ${
                  activeTab === 'transactions'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                {t('transactions.title')}
              </button>
              <button
                onClick={() => setActiveTab('invoices')}
                className={`text-sm font-medium pb-1 border-b-2 transition-colors ${
                  activeTab === 'invoices'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                {t('documents.title')}
              </button>
            </div>
          </div>

          {activeTab === 'transactions' ? (
            transactions.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('transactions.noTransactions')}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {transactions.map((tx) => (
                  <div key={tx.id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getTransactionIcon(tx.type)}
                      <div>
                        <p className="font-medium text-gray-900">{tx.description}</p>
                        <p className="text-sm text-gray-500">{new Date(tx.created_at).toLocaleDateString()} at {new Date(tx.created_at).toLocaleTimeString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount >= 0 ? '+' : ''}€{(tx.amount / 100).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">{t('transactions.balance')}: €{(tx.balance_after / 100).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            invoices.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('documents.noDocuments')}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Document type icon */}
                      <div className={`p-2 rounded-lg ${
                        invoice.document_type === 'RECEIPT' ? 'bg-green-100' :
                        invoice.document_type === 'CREDIT_NOTE' ? 'bg-orange-100' :
                        'bg-blue-100'
                      }`}>
                        {invoice.document_type === 'RECEIPT' ? (
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : invoice.document_type === 'CREDIT_NOTE' ? (
                          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{invoice.description}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            invoice.document_type === 'RECEIPT' ? 'bg-green-100 text-green-700' :
                            invoice.document_type === 'CREDIT_NOTE' ? 'bg-orange-100 text-orange-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {invoice.document_type === 'RECEIPT' ? t('documents.receipt') :
                             invoice.document_type === 'CREDIT_NOTE' ? t('documents.creditNote') :
                             t('documents.invoice')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{invoice.id} • {new Date(invoice.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`font-medium ${invoice.document_type === 'CREDIT_NOTE' ? 'text-green-600' : 'text-gray-900'}`}>
                          {invoice.document_type === 'CREDIT_NOTE' ? '+' : ''}€{invoice.amount.toFixed(2)}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                          invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {invoice.status}
                        </span>
                      </div>
                      {invoice.pdf_path && (
                        <button
                          onClick={() => handleDownloadInvoice(invoice.id)}
                          disabled={downloadingInvoice === invoice.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          title={t('documents.downloadPdf')}
                        >
                          {downloadingInvoice === invoice.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                          <span>{t('documents.pdf')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Top Up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('topUp.title')}</h3>
            <p className="text-gray-600 mb-4">
              {t('topUp.description')}
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('topUp.amount')}</label>
              <div className="flex gap-2 mb-3">
                {[10, 25, 50, 100].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setTopUpAmount(amount)}
                    className={`flex-1 py-2 px-3 rounded-lg font-medium transition-colors ${
                      topUpAmount === amount
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    €{amount}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="5"
                max="10000"
                step="0.01"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder={t('topUp.customAmount')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('topUp.minMax')}</p>
            </div>

            {/* Payment Method Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('topUp.paymentMethod')}</label>
              {paymentMethods.length === 0 ? (
                <button
                  onClick={() => { setShowTopUpModal(false); setShowAddCardModal(true); }}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-600 transition-colors"
                >
                  <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('topUp.addPaymentMethod')}
                </button>
              ) : (
                <div className="space-y-2">
                  {paymentMethods.map((pm) => (
                    <button
                      key={pm.id}
                      onClick={() => setSelectedPaymentMethodId(pm.id)}
                      className={`w-full p-3 rounded-lg border-2 flex items-center gap-3 transition-colors ${
                        selectedPaymentMethodId === pm.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {getCardIcon(pm.brand)}
                      <span className="font-medium">•••• {pm.last4}</span>
                      <span className="text-sm text-gray-500">{pm.exp_month.toString().padStart(2, '0')}/{pm.exp_year}</span>
                      {pm.is_default && (
                        <span className="ml-auto text-xs text-emerald-600">{t('paymentMethods.default')}</span>
                      )}
                    </button>
                  ))}
                  <button
                    onClick={() => { setShowTopUpModal(false); setShowAddCardModal(true); }}
                    className="w-full p-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {t('topUp.addNewCard')}
                  </button>
                </div>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{t('topUp.topUpAmount')}</span>
                <span className="font-medium">€{topUpAmount.toFixed(2)}</span>
              </div>
              {selectedPaymentMethodId && (
                <div className="flex justify-between mb-2 text-sm text-gray-500">
                  <span>{t('topUp.paymentMethod')}</span>
                  <span>{(() => {
                    const pm = paymentMethods.find(p => p.id === selectedPaymentMethodId);
                    return pm ? `${pm.brand} •••• ${pm.last4}` : 'Card';
                  })()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-500">
                <span>{t('topUp.newBalance')}</span>
                <span>€{(((balance?.amount || 0) / 100) + topUpAmount).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowTopUpModal(false)}
                disabled={isTopingUp}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('topUp.cancel')}
              </button>
              <button
                onClick={handleTopUp}
                disabled={isTopingUp || topUpAmount < 5 || (paymentMethods.length > 0 && !selectedPaymentMethodId)}
                className="flex-1 py-2 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {isTopingUp ? 'Processing...' : t('topUp.pay', { amount: topUpAmount.toFixed(2) })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {selectedPlan.price_monthly > (currentPlan?.price_monthly || 0) ? 'Upgrade' : 'Change'} to {selectedPlan.name}
            </h3>
            <p className="text-gray-600 mb-4">
              {selectedPlan.price_monthly > 0
                ? `€${selectedPlan.price_monthly}/month will be charged from your balance.`
                : 'This is a free plan with no monthly charge.'}
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{t('plan.currentPlan')}</span>
                <span className="font-medium">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between mb-2" style={{ display: 'none' }}>
                <span className="text-gray-600">{t('plan.monthlyPrice')}</span>

                <span className="font-medium">€{selectedPlan.price_monthly}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('balance.yourBalance')}</span>
                <span className={`font-medium ${
                  (balance?.amount || 0) >= selectedPlan.price_monthly * 100
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {balance?.amount_formatted || '€0.00'}
                </span>
              </div>
            </div>

            {selectedPlan.price_monthly > 0 && (balance?.amount || 0) < selectedPlan.price_monthly * 100 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">
                  {t('plan.insufficientBalanceTopUp', { amount: (selectedPlan.price_monthly - (balance?.amount || 0) / 100).toFixed(2) })}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('topUp.cancel')}
              </button>
              <button
                onClick={confirmUpgrade}
                disabled={isSubscribing !== null || (selectedPlan.price_monthly > 0 && (balance?.amount || 0) < selectedPlan.price_monthly * 100)}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isSubscribing ? t('plan.processing') : tc('buttons.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      {showAddCardModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('addCard.title')}</h3>
            <p className="text-gray-600 mb-4">
              {t('addCard.description')}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('addCard.cardNumber')}</label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={(e) => {
                    // Format with spaces
                    const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                    setCardNumber(val.replace(/(\d{4})/g, '$1 ').trim());
                  }}
                  placeholder="4242 4242 4242 4242"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">{t('addCard.demo')}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('addCard.expiryMonth')}</label>
                  <select
                    value={cardExpMonth}
                    onChange={(e) => setCardExpMonth(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('addCard.monthPlaceholder')}</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('addCard.expiryYear')}</label>
                  <select
                    value={cardExpYear}
                    onChange={(e) => setCardExpYear(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('addCard.yearPlaceholder')}</option>
                    {Array.from({ length: 10 }, (_, i) => 2024 + i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('addCard.cardholderName')}</label>
                <input
                  type="text"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder={t('addCard.namePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 mt-4 mb-4">
              <p className="text-xs text-blue-700">
                <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                {t('addCard.demoNotice')}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddCardModal(false);
                  setCardNumber('');
                  setCardExpMonth('');
                  setCardExpYear('');
                  setCardholderName('');
                }}
                disabled={isAddingCard}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('topUp.cancel')}
              </button>
              <button
                onClick={handleAddCard}
                disabled={isAddingCard || !cardNumber || !cardExpMonth || !cardExpYear}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {isAddingCard ? t('addCard.adding') : t('addCard.addCard')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
